import React, { useEffect, useState } from 'react';

import { c } from 'ttag';

import {
    AuthenticationStore,
    Badge,
    Button,
    Card,
    CardFields,
    Checkbox,
    Modal,
    useAuthentication,
    useCurrentUser,
    useLoading,
    useNotifications,
} from '@freelancelabs/components';
import { clsx } from '@freelancelabs/utils';

import { recordMfaEnrolment } from '../../api/mfaEnrolment';
import TotpPairing, { TOTP_CODE_LENGTH } from '../../app/mfa/TotpPairing';

const AccountPage = () => {
    const {
        fetchMfaSettings,
        setEmailMfaEnabled,
        setupTotp,
        verifyTotpSetup,
        disableTotp,
        forgotPassword,
        getAuthorizationToken,
    } = useAuthentication() as AuthenticationStore;
    const { email, emailVerified, federated } = useCurrentUser();
    const { createNotification } = useNotifications();
    const [isLoaded, setIsLoaded] = useState(false);
    const [mfaEnabled, setMfaEnabled] = useState(false);
    const [totpEnabled, setTotpEnabled] = useState(false);
    const [saving, withSaving] = useLoading();
    const [sendingReset, withSendingReset] = useLoading();

    // enrolement TOTP
    const [totpSetup, setTotpSetup] = useState<{ secret: string; uri: string } | null>(null);
    const [totpCode, setTotpCode] = useState('');
    const [totpError, setTotpError] = useState('');
    const [enrolling, withEnrolling] = useLoading();

    useEffect(() => {
        const run = async () => {
            try {
                const settings = await fetchMfaSettings();
                setMfaEnabled(settings.email);
                setTotpEnabled(settings.totp);
            } finally {
                setIsLoaded(true);
            }
        };
        void run();
    }, []);

    const handleMfaToggle = (enabled: boolean) => {
        const run = async () => {
            await setEmailMfaEnabled(enabled);
            setMfaEnabled(enabled);
            createNotification({
                type: 'success',
                text: enabled
                    ? c('Mfa notification').t`Two-factor authentication enabled.`
                    : c('Mfa notification').t`Two-factor authentication disabled.`,
            });
        };
        withSaving(run()).catch(() => {
            createNotification({
                type: 'danger',
                text: c('Mfa notification').t`Unable to update two-factor authentication.`,
            });
        });
    };

    const handleTotpStart = () => {
        if (!email) {
            return;
        }
        setTotpCode('');
        setTotpError('');
        withEnrolling(setupTotp(email))
            .then(setTotpSetup)
            .catch(() => {
                createNotification({
                    type: 'danger',
                    text: c('Totp notification').t`Unable to start the authenticator app setup.`,
                });
            });
    };

    const handleTotpVerify = () => {
        if (enrolling || totpCode.length < TOTP_CODE_LENGTH) {
            return;
        }
        setTotpError('');
        withEnrolling(verifyTotpSetup(totpCode))
            .then(async () => {
                setTotpSetup(null);
                setTotpEnabled(true);
                // L'EVENEMENT, la premiere mise en conformite. Silencieux par
                // construction : le facteur est pose dans le pool quoi qu'il arrive ici.
                const token = await getAuthorizationToken();
                if (token) {
                    recordMfaEnrolment(token);
                }
                createNotification({
                    type: 'success',
                    text: c('Totp notification').t`Authenticator app enabled.`,
                });
            })
            .catch(() => {
                setTotpError(c('Mfa error').t`Invalid code.`);
                setTotpCode('');
            });
    };

    const handleTotpDisable = () => {
        withSaving(disableTotp())
            .then(() => {
                setTotpEnabled(false);
                // RIEN A ENREGISTRER ICI, et c'est le changement de conception : l'etat se
                // lit dans le pool, qui dira de lui-meme qu'il n'y a plus de facteur. La
                // date de mise en conformite, elle, ne s'efface pas : un evenement ne se
                // de-produit pas, et le retrait est un evenement a lui dont la place est
                // le journal d'activite, pas l'ecrasement d'une date.
                createNotification({
                    type: 'success',
                    text: c('Totp notification').t`Authenticator app disabled.`,
                });
            })
            .catch(() => {
                createNotification({
                    type: 'danger',
                    text: c('Mfa notification').t`Unable to update two-factor authentication.`,
                });
            });
    };

    const handlePasswordReset = () => {
        if (!email || sendingReset) {
            return;
        }
        withSendingReset(forgotPassword(email))
            .then(() => {
                createNotification({
                    type: 'success',
                    text: c('Account notification').t`A password reset email has been sent.`,
                });
            })
            .catch((error: Error) => {
                // rate limiting anti-abus de cognito, par utilisateur, non configurable
                const isRateLimited = error?.name === 'LimitExceededException';
                createNotification({
                    type: 'danger',
                    text: isRateLimited
                        ? c('Account notification').t`Too many attempts, please try again in a few minutes.`
                        : c('Account notification').t`Unable to send the reset email.`,
                });
            });
    };

    // le reset par email reste possible avec le TOTP ; il est bloque par cognito
    // uniquement quand l'email lui-meme est le second facteur
    /** Y a-t-il une seconde etape, quelle qu'elle soit. */
    const protectedNow = totpEnabled || mfaEnabled;
    /**
     * La ou les methodes en place, nommees. Les deux peuvent l'etre ensemble : Cognito tient
     * deux preferences INDEPENDANTES, ce qu'un groupe radio aurait fait dire faux.
     */
    const methodLabel =
        totpEnabled && mfaEnabled
            ? c('Security state').t`Authenticator app and email code`
            : totpEnabled
              ? c('Security state').t`Authenticator app`
              : mfaEnabled
                ? c('Security state').t`Email code`
                : c('Security state').t`No second step yet`;

    const resetBlockedByMfa = mfaEnabled && !totpEnabled;

    /**
     * CE QUI REPOND DE L'ADRESSE, en une pastille ou rien du tout.
     *
     * LA SESSION FEDEREE PASSE EN PREMIER, et ce n'est pas un ordre arbitraire : mesure
     * sur un jeton reel, une session SSO porte `email_verified: false`. Lire le claim
     * d'abord ferait donc taire la pastille pour TOUS les internes, dont l'adresse est
     * precisement celle dont leur entreprise repond. Le claim ne dit rien d'eux, il dit
     * seulement que Cognito n'a pas envoye de courriel de verification.
     *
     * Les deux pastilles sont de la MEME couleur : elles disent le meme fait, quelqu'un
     * repond de cette adresse. Deux teintes suggereraient deux niveaux de confiance, ce
     * qui serait une hierarchie que rien ne fonde.
     *
     * Et rien ne s'affiche autrement : le claim absent ou faux sur un compte natif ne
     * veut pas dire « non verifiee », il veut dire que ce jeton ne l'affirme pas. Cet
     * ecran n'offrant aucun geste pour verifier une adresse, une mise en garde y serait
     * une inquietude qu'on ne peut pas lever.
     */
    const vouchedFor = federated
        ? c('Account field').t`Verified by your company`
        : emailVerified === true
          ? c('Account field').t`Verified`
          : null;

    return (
        /* L'enveloppe et le corps des pages des DEUX autres applications, et non des classes
           a soi : cette page vivait sur `account-page` et `account-card`, definis dans le
           `login.scss` de l'application. L'exception documentee du depot couvre les ecrans
           PUBLICS d'`account` ; celui-ci est prive, et son look n'avait donc aucune raison de
           vivre hors du design system. */
        <div className="flex flex-1 flex-col px-6 pt-8 pb-12">
            <div className="fl-page-body flex flex-1 flex-col gap-4">
                <header className="fl-page-body__head">
                    <h1 className="fl-page-title">{c('Account title').t`My profile`}</h1>
                    <p className="mt-1 text-xs text-text-500">
                        {c('Account title').t`Your identity and how you sign in.`}
                    </p>
                </header>

                <Card title={c('Account section').t`Identification`}>
                    {/* Attention, CETTE CARTE NE PORTE PLUS DE NOM DU TOUT, decide le
                        2026-08-29 : l'en-tete avatar + nom de la maquette est retire, et il
                        n'y a pas de champ « Nom » pour le remplacer. Le nom vient de la
                        source et ne se saisit pas ici ; l'adresse de connexion est ce qui
                        identifie la personne sur cet ecran, et c'est le seul champ servi.

                        Le commentaire d'avant justifiait l'absence de champ par la presence
                        de l'en-tete. Cet argument est mort avec lui : ce qui tient encore
                        est que rien ici n'ecrit un nom, donc en afficher un vide ou un tiret
                        affirmerait quelque chose sur la personne. */}
                    <CardFields
                        className="fl-card__fields--single mt-5"
                        fields={[
                            {
                                label: c('Account field').t`Sign-in email`,
                                value: email ? (
                                    <span className="flex flex-wrap items-center gap-2">
                                        {email}
                                        {vouchedFor && (
                                            <Badge category="success" size="small">
                                                {vouchedFor}
                                            </Badge>
                                        )}
                                    </span>
                                ) : undefined,
                            },
                        ]}
                    />

                    {/* Attention, LA MAQUETTE EN MONTRE QUATRE et cet ecran n'en sert qu'un.
                        « Fonction », « Societe » et « Role portail » vivent dans
                        `client-hub-api`, que cette application n'interroge pas : elle n'a pas
                        d'Apollo, et son role est de faire entrer les gens.

                        La phrase qui l'expliquait au lecteur est retiree le 2026-08-29,
                        decide a l'ecran : sous un champ unique elle pesait plus lourd que ce
                        qu'elle annotait. Ce qu'elle empechait tient toujours : ces trois
                        champs ne se rendent PAS en tirets, un tiret disant « vous n'avez pas
                        de fonction », une affirmation fausse sur la personne la ou le manque
                        est le notre. Les ajouter demande de les servir, pas de les afficher
                        vides. */}
                </Card>

                <Card title={c('Security title').t`Two-step verification`}>
                    <p className="text-xs text-text-500">
                        {c('Security title')
                            .t`A second proof of identity is asked for at each login. Both methods can be on at once.`}
                    </p>

                    {/* CE QUI EST EN PLACE, avant les reglages qui le changent : reprise de
                        `compte-mfa-actuel`. Une liste de reglages ne dit pas lequel est actif
                        sans qu'on lise les deux.

                        Attention, LE TROISIEME CAS EST LE PLUS UTILE : tant que la lecture du
                        pool n'a pas repondu, on ne dit NI « activee » NI « aucune ». Annoncer
                        « aucune » sur une lecture en cours ferait croire a quelqu'un de
                        conforme qu'il ne l'est pas. */}
                    {/* L'ETAT SE LIT SANS PHRASE, reprise de `compte-mfa-actuel` : une
                        pastille, la methode en gras, la cible en gris. Une phrase parmi
                        d'autres phrases ne se distingue pas de la consigne qui l'entoure, et
                        c'est pourtant le seul fait que cette carte doit donner d'un coup
                        d'oeil.

                        Attention, LE TROISIEME CAS EST LE PLUS UTILE : tant que la lecture du
                        pool n'a pas repondu, on ne dit NI « activee » NI « aucune ». Annoncer
                        « aucune » sur une lecture en cours ferait croire a quelqu'un de
                        conforme qu'il ne l'est pas, et une pastille verte l'affirmerait plus
                        fort qu'une phrase. */}
                    <div className="fl-setting-state mt-3">
                        {!isLoaded ? (
                            <span className="text-text-600">{c('Security state').t`Reading your settings…`}</span>
                        ) : (
                            <>
                                <Badge size="small" category={protectedNow ? 'success' : 'default'}>
                                    {protectedNow ? c('Security state').t`On` : c('Security state').t`Off`}
                                </Badge>
                                <b>{methodLabel}</b>
                                {mfaEnabled && !totpEnabled && email && (
                                    <span className="text-xs text-text-600">{email}</span>
                                )}
                            </>
                        )}
                    </div>

                    <div className="mt-3 flex flex-col gap-3">
                        <div className={clsx('fl-setting-row', totpEnabled && 'fl-setting-row--active')}>
                            <span className="fl-setting-row__text">
                                <b className="fl-setting-row__title">
                                    {c('Totp section').t`Authenticator app (TOTP)`}
                                    {/* « Recommande » est deja dans la phrase d'aide ; en
                                        pastille il se voit avant qu'on la lise, ce qui est
                                        tout l'interet quand deux reglages se ressemblent. Il
                                        disparait des que le reglage est en place : conseiller
                                        ce qui est deja fait est du bruit. */}
                                    {!totpEnabled && (
                                        <Badge size="small" category="info">
                                            {c('Totp section').t`Recommended`}
                                        </Badge>
                                    )}
                                </b>
                                <span className="fl-setting-row__help">
                                    {c('Totp section help')
                                        .t`Recommended method: at each login, enter the code generated by your authenticator app (Google Authenticator, Microsoft Authenticator, FreeOTP...).`}
                                </span>
                            </span>
                            {totpEnabled ? (
                                <Button
                                    category="ghost"
                                    onClick={handleTotpDisable}
                                    loading={saving}
                                    disabled={!isLoaded}
                                >
                                    {c('Action').t`Disable`}
                                </Button>
                            ) : (
                                <Button onClick={handleTotpStart} loading={enrolling} disabled={!isLoaded || !email}>
                                    {c('Action').t`Enable`}
                                </Button>
                            )}
                        </div>

                        <div className={clsx('fl-setting-row', mfaEnabled && 'fl-setting-row--active')}>
                            <span className="fl-setting-row__text">
                                <b className="fl-setting-row__title">{c('Mfa section').t`Email verification code`}</b>
                                <span className="fl-setting-row__help">
                                    {c('Mfa section help')
                                        .t`Add an extra layer of security: at each login, a verification code will be sent to your email address.`}
                                </span>
                            </span>
                            <Checkbox
                                id="email-mfa"
                                checked={mfaEnabled}
                                disabled={!isLoaded}
                                loading={saving}
                                onChange={(event) => handleMfaToggle(event.target.checked)}
                                label={c('Mfa toggle').t`Enable`}
                            />
                        </div>
                    </div>
                </Card>

                <Card title={c('Account section').t`Password`}>
                    {/* Attention, LA MAQUETTE DESSINE UN FORMULAIRE et il n'a aucun mecanisme :
                        trois champs (actuel, nouveau, confirmation), une jauge et une liste de
                        regles. Le store d'authentification n'expose PAS de changement de mot de
                        passe en session, seulement l'envoi d'un lien. Rendre les trois champs
                        pour ne rien pouvoir en faire serait offrir un geste impossible. */}
                    <div className="fl-setting-row">
                        <span className="fl-setting-row__text">
                            <b className="fl-setting-row__title">{c('Account section').t`Change my password`}</b>
                            <span className="fl-setting-row__help">
                                {resetBlockedByMfa
                                    ? // contrainte Cognito : l'email ne peut pas etre a la fois second facteur et canal de recuperation
                                      c('Account help')
                                          .t`Unavailable while the email verification code is enabled: turn it off for a moment to reset your password by email.`
                                    : c('Account help').t`We will send you an email to set a new password.`}
                            </span>
                        </span>
                        <Button
                            category="ghost"
                            onClick={handlePasswordReset}
                            loading={sendingReset}
                            disabled={!email || resetBlockedByMfa}
                        >
                            {c('Action').t`Reset my password`}
                        </Button>
                    </div>
                </Card>
            </div>

            <Modal
                open={Boolean(totpSetup)}
                onClose={() => setTotpSetup(null)}
                title={c('Totp title').t`Set up your authenticator app`}
                size={600}
                panelProps={{ className: 'modal_panel-container mfa-modal' }}
            >
                {totpSetup && (
                    <form
                        name="totp-setup-form"
                        onSubmit={(event) => {
                            event.preventDefault();
                            handleTotpVerify();
                        }}
                        method="post"
                    >
                        {/* Les deux etapes numerotees de la maquette V3, partagees avec
                            la barriere d'entree : meme geste, donc meme contenu. Ce qui
                            reste propre a cet ecran est le CADRE, une modale avec sa croix
                            et son « Annuler », parce qu'ici poser un facteur est un CHOIX.
                            La clef y est desormais groupee par quatre : elle se recopie a
                            la main quand le scan echoue. */}
                        <TotpPairing
                            uri={totpSetup.uri}
                            secret={totpSetup.secret}
                            code={totpCode}
                            onCode={setTotpCode}
                            error={totpError}
                            busy={enrolling}
                        />
                        <div className="mfa-actions">
                            <Button
                                category="ghost"
                                type="button"
                                onClick={() => setTotpSetup(null)}
                                disabled={enrolling}
                            >
                                {c('Action').t`Cancel`}
                            </Button>
                            <Button type="submit" loading={enrolling} disabled={totpCode.length < TOTP_CODE_LENGTH}>
                                {c('Action').t`Verify`}
                            </Button>
                        </div>
                    </form>
                )}
            </Modal>
        </div>
    );
};

export default AccountPage;
